import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ReplaySubject} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {PersonalDataChange} from "../../../shared/interface/personalDataChange";
import {PersonalDataChangeService} from "../../../shared/services/personal-data-change.service";
import {MatSort, Sort} from "@angular/material/sort";

@Component({
  selector: 'app-all-personal-data-change-list',
  templateUrl: './all-personal-data-change-list.component.html',
  styleUrls: ['./all-personal-data-change-list.component.scss']
})
export class AllPersonalDataChangeListComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() typeKey?: string[] = null;
  @Input() statusKey?: string[] = null;
  @Input() masterModule?: string = null;
  @Input() insuranceCompanyId?: number = null;
  @Input() displayedColumns: string[] = ['ofwcaFullName', 'status' , 'insuranceCompany', 'dateOfSubmission', 'documentsDelivered', 'assetsRegional'];

  initialFilters: any;
  personalDataChanges: PersonalDataChange[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  filterStatus = false;
  sortBy = 'dateOfSubmission';
  sortOrder = null;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private personalDataChangeService: PersonalDataChangeService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.personalDataChangeService.getPersonalDataChangeListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<PersonalDataChange>) => {
          this.personalDataChanges = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      ofwca: 'string',
      gte$dateOfSubmission: 'date',
      lte$dateOfSubmission: 'date',
      documentsDelivered: 'int',
      assetsRegional: 'string[]',
      in$insuranceCompany: 'int[]',
      status: 'int',
    }, filters);
    this.sortBy = filters?.sortBy || 'dateOfSubmission';
    this.sortOrder = filters?.sortOrder ? filters.sortOrder.toUpperCase() : 'DESC';
  }

  ngAfterViewInit(): void {
    this.sort.sortChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: Sort) => {
        this.sortChanged(event);
      });
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.typeKey = this.typeKey ?? data.typeKey ?? null;
        this.displayedColumns = data.displayedColumns ?? this.displayedColumns;

        this.filterStatus = !this.statusKey;

        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.sortBy = state.params?.sortBy ? state.params.sortBy : 'dateOfSubmission';
            this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'DESC';
            this.loadListData();
          })
        ;
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }
    if (null !== this.typeKey) {
      filters.typeKey = this.typeKey;
    }

    return filters;
  }

  loadListData(): void {
    this.personalDataChangeService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
      );
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.personalDataChangeService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
          this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: { jobId: response.uuid, showProgress: false },
          });
        },
        (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), { duration: 2000 });
        }
      );
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  sortChanged(event: Sort): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        sortBy: event.active,
        sortOrder: event.direction.toUpperCase(),
        ...this.filtersForm.value
      })
    ]);
  }
}
